/* 
 * React component to show a calendar for one or more people.
 * The fullCallendar component is used; see https://fullcalendar.io/docs#toc
 * 
 */

import React, {useState, useRef, useCallback, useEffect, useMemo}
                                        from 'react'

import FullCalendarImport               from '@fullcalendar/react'; // might switch for a test
import resourceTimeGridPlugin           from '@fullcalendar/resource-timegrid';
import resourceDayGridPlugin            from '@fullcalendar/resource-daygrid';
import interactionPlugin                from '@fullcalendar/interaction';

import TeamCalendarOverlay              from './TeamCalendarOverlay'
import  useBookingState, {bookingActive}            
                                        from '../Booking/bookingReducer';
import {LoadingIndicator as Loading}    from '../../lib/iprs-react-library/src/index.js';
import teamEventSource                  from './TeamCalendarEventSource';
import eventContent                     from './TeamCalendarEventRender';
import handleDrop                       from './TeamCalendarDropHandler';
import {getDateStr}                     from '../../lib/iprs-react-library/src/index.js';
import userLoggedInHasRole              from '../Util/userLoggedInHasRole';
import {teamDayStartTime, teamSlotMinTime}
                                        from '../Util/earlyStart';
import WebAPIWarning                    from '../Util/WebAPIWarning';
import {getTeamWorkingHours, WORKING_HOURS_IMPACT}
                                        from '../WorkingHours/workingHoursApi';
import {businessHoursForDate, toDateString, addDays, ALL_DAY_BUSINESS_HOURS,
        workingHoursBackgroundEvents}
                                        from '../WorkingHours/workingHoursPattern';

/* This is the license key which allows use of paid for views of the calendar.
 * See env.local and .env files. */
const schedulerLicenseKey =  process.env.REACT_APP_SCHEDULER_LICENSE_KEY;

const twentyMinTeams           = (process.env.REACT_APP_CHRONOS_TWENTY_MINUTE_DEFAULT_TEAMS?? '')
    .split(',', 100)
    .map(i => Number.parseInt(i, 10)); 

    const fifteenMinTeams           = (process.env.REACT_APP_CHRONOS_FIFTEEEN_MINUTE_DEFAULT_TEAMS?? '')
    .split(',', 100)
    .map(i => Number.parseInt(i, 10)); 

const TeamCalendar = ({api, state, eventDispatch, _testFullCalendar = null}) => {
    
    // can use a stub for Full calendar in tests
    const FullCalendar = _testFullCalendar?? FullCalendarImport;

    const calendarApi                                   = useRef()
    
    const { team: { teamID, teams = [], date = new Date() },
            appType: { appointmentTypes = [] } = {} }   = state;

    if(api){ // avoid conflict where this hasn't been set in state
        state.api = api;
    }
        
    const teamName = (0 === teamID)? 0: teams.filter( t => t.pkTeamId === teamID )[0]?.TeamName;
    const minInterval = (0 === teamID)? 0: teams.filter( t => t.pkTeamId === teamID )[0]?.CalendarGridIntervalMins;
    
    /* The start of the working day for this team; earlier than normal for the
     * teams listed in REACT_APP_CHRONOS_EARLY_START_TEAMS. */
    const dayStartTime = teamDayStartTime(teamID);

    let slotInterval, slotLabelInterval, omitZeroMinute, slotMinTime;
    /* temporary fix based on teamID in UAT and production for VH which want to 
     * align with main appType which is 20mins */
    if(twentyMinTeams.includes(teamID)){
        slotMinTime         = teamSlotMinTime(teamID, '07:40');
        slotInterval        = '00:20';
        slotLabelInterval   = slotInterval;
        omitZeroMinute      = false;
    }else if(fifteenMinTeams.includes(teamID)){
        slotMinTime         = teamSlotMinTime(teamID, '07:45');
        slotInterval        = '00:15';
        slotLabelInterval   = slotInterval;
        omitZeroMinute      = false;
    }
    else{
        slotMinTime         = dayStartTime;
        slotInterval        = '00:30';
        slotLabelInterval   = '01:00';
        omitZeroMinute      = true;
    }
    
    const [bookingState,]                               = useBookingState(state, eventDispatch)
    
    const [rerender, doRerender]                        = useState(1)
    
    const [loading, setLoading]                                   = useState(false);
    
    /* 
     * This is the last event fired by the calendar, wrapped in an object with 
     * an action field so we can diaspay an overlay.
     */
    const [calendarEvent, setCalendarEvent]             = useState(null)
    
    /* we either show availability in the background or appointments in background*/
    const [rotaMode,    setRotaMode]                    = useState(false);
    const [qeMode,      setQEMode]                      = useState(true);
    const [hideEmpty,   setHideEmpty]                   = useState(false);

    const hasDiaryRotaPerm = userLoggedInHasRole(api, 'Diary Rota');
    
    const handleModeChange = e => {
        
        if( 'rotaMode' === e.target.name ){
            setRotaMode( e.target.checked )
        }else if ('hideEmpty') {
            setHideEmpty( e.target.checked );
        }else{
            setQEMode( e.target.checked )
        }
        calendarApi.current.getApi().refetchEvents()
    }
    
    useEffect(() => {
        const refreshTime = Number.parseInt(
            process.env.REACT_APP_IPRS_WEB_CALENDAR_REFRESH_PERIOD_MS
        );
        if (refreshTime > 0) {
            const timedReload = setInterval(() => {
                const current = calendarApi.current;
                current?.getApi()?.refetchEvents();
            }, refreshTime);
            return () => clearInterval(timedReload);
        }
    }, [calendarApi]);
    
    // used to filter which people are displayed
    const [teamFilter, setTeamFilter]                   = useState([]) 
    
    const eventSource = useCallback( (info, successCallback, failureCallback) => {
        
        teamEventSource({cal: {info, successCallback, failureCallback},
                         other: { teamID, api, rotaMode, teamFilter, bookingState,
                                  appointmentTypes } } );
                     
    }, [teamID, api, rotaMode, teamFilter, appointmentTypes, bookingState]);
    
    /* Shading for people without working hours; also used on the days someone
     * with working hours has no pattern in effect. */
    const defaultBusinessHours = useMemo( () => ({
        // days of week. an array of zero-based day of week integers (0=Sunday)
        daysOfWeek: [ 1, 2, 3, 4, 5, 6 ], // Monday - Saturday
        startTime: dayStartTime,
        endTime: '18:00', // an end time (6pm in this example)
    }), [dayStartTime]);

    /* Working hours are display only. Failure to load them must not stop the
     * calendar, so it is treated as nobody having any, with a warning the
     * user can open to see why. WebAPI may be unreachable (e.g. off the IPRS
     * network), and each attempt can take the full timeout while holding up
     * the people list, so after one failure stop asking until "Try again". */
    const [workingHoursError, setWorkingHoursError]     = useState(null);
    const workingHoursFailed                           = useRef(false);

    const loadWorkingHours = useCallback( (start, end) => {
        if( workingHoursFailed.current ){
            return Promise.resolve( [] );
        }
        return getTeamWorkingHours(api, teamID, toDateString(start), toDateString(addDays(end, -1)))
            .then( ({data, error}) => {
                if( error ){
                    console.log('Working hours not loaded', error.detail ?? error);
                    workingHoursFailed.current = true;
                    setWorkingHoursError( error );
                    return [];
                }
                return data ?? [];
            } );
    }, [api, teamID]);

    const retryWorkingHours = () => {
        workingHoursFailed.current = false;
        setWorkingHoursError( null );
        const calendar = calendarApi.current?.getApi();
        calendar?.refetchResources();
        calendar?.refetchEvents();
    };

    /* Shading for people with working hours, worked out per date so that a
     * change part way through the view shows from the right day. With "Hide
     * People not Working" ticked FullCalendar would count these as events and
     * show everyone with working hours, so resourceSource falls back to
     * businessHours instead. */
    const workingHoursEventSource = useCallback( (info, successCallback) => {
        if( hideEmpty ){
            successCallback( [] );
            return;
        }
        loadWorkingHours( info.start, info.end )
            .then( hours => successCallback(
                workingHoursBackgroundEvents( hours, info.start, info.end, defaultBusinessHours ) ) );
    }, [hideEmpty, loadWorkingHours, defaultBusinessHours]);

    /* Both sources go in eventSources. FullCalendar treats the events and
     * eventSources options as each holding the full list, so using both
     * removes one's source whenever the other changes. */
    const eventSources = useMemo( () => [ eventSource, workingHoursEventSource ],
        [eventSource, workingHoursEventSource]);

    const resourceSource = useCallback((info, successCallback, failureCallback) => {

        /* People with working hours get businessHours that shade nothing, as
         * workingHoursEventSource shades them per date. With "Hide People not
         * Working" ticked they instead get businessHours from the pattern in
         * effect on the view's first day, so a change part way through the
         * view only shows from the next one. People without working hours keep
         * the calendar default. */
        const start = info?.start ?? new Date();
        const end   = info?.end ?? addDays(start, 1);
        const viewStart = toDateString(start);
        const workingHours = loadWorkingHours( start, end );

        Promise.all([ api.getTeamMemberList(teamID), workingHours ])
            .then( ([response, hours]) => [ response.apiResult, hours ] )
            .then( ([apiResult, hours]) => [ apiResult.filter(
                // if there's a team filter then only include results matching the filter
                r => !teamFilter.length || teamFilter.includes( r.pkPartyID )
            ), hours ] )
            .then( ([apiResult, hours]) => {
                return apiResult.map( a => {
                    const resource = {id: a.pkPartyID, title: a.FirstName + ' ' + a.LastName};
                    const party = hours.find( h => h.PartyID === a.pkPartyID );
                    const businessHours = party && ( hideEmpty
                        ? businessHoursForDate( party.Patterns, viewStart )
                        : ALL_DAY_BUSINESS_HOURS );
                    if( businessHours ){
                        resource.businessHours = businessHours;
                    }
                    return resource;
                } ) } )
            .then( resources => {
                successCallback( resources )
    } )
            .catch( error =>  failureCallback( error ) )
    }, [teamID, teamFilter, api, hideEmpty, loadWorkingHours] )
    
    
    /* Called by the calendar when the user selects a region of the calendar; 
     * this selects calendar time not a particular event. */
    const handleSelectRegion = eventInfo => {
        
        let action = null;
        if( !rotaMode && bookingActive( bookingState ) ){
       
            // overlay will allow time to be fine edited
            action = 'BookingProcessOverlay';
        }else{
            
            action = rotaMode? 'selectRotaRegion': 'selectRegion'
        
            // action to say that we have selected empty space
        }
        setCalendarEvent( { action, event: eventInfo } )
    }
    
    /* Handles a click on an event. Currently we just delete the event with no
     * confirmation step */
    const handleClick = eventInfo => {

        // Working hours shading isn't a diary item
        if( eventInfo.event.extendedProps.isWorkingHours ){
            return;
        }

        let action = rotaMode? 'rotaEventClick': 'eventClick'
        
        if( !rotaMode ){
            if ( eventInfo.event.extendedProps.apiEvent.BookingID ){
                
                
                action = 'bookingEventClick'
                
                
            }else{
                action = 'NCAEventClick'; // Non Clinical Activity
            }
        }else{
                action = 'rotaEventClick'
        }
        
        // action to say that we have clicked something not selected empty space
        setCalendarEvent( { action, event: eventInfo.event } )
    }
    
    /* Called by the calendar when the user resizes an event; i.e when they drag 
     * the start or end of the event */
    const handleResize = eventResizeInfo => {
        
        const oldEvent =  eventResizeInfo.oldEvent
        const event    =  eventResizeInfo.event
        
        const oldAPIEvent = oldEvent.extendedProps.apiEvent;
        const {
            RotaID: rotaID, PartyID: partyID, RotaTypeID: rotaTypeID,
            Description: desc               = '',
            Title: title                    = '',
            Available: available
        }                                               = oldAPIEvent
        
        if( rotaMode ){
            api.updateRota( rotaID,     
                            event.start, 
                            event.end, 
                            null,
                            partyID,
                            rotaTypeID)
            .then( response => {
            
                 /* hack to get REACT to re-render*/
                doRerender( rerender+1 )
            })
            .catch(e => {
                console.log(e)
            })   
        }else{
            api.updateTask(rotaID, event.start, event.end, desc, partyID, rotaTypeID,
                       available, title)
            .then( response => {
            
                 /* hack to get REACT to re-render*/
                doRerender( rerender+1 )
            })
            .catch(e => {
                console.log(e)
            })
        }
        
    }
    
    
    
    const dayHeaderContent = (data) => {
        return getDateStr( data.date );
    }
    
    const handleResourceClick = e => {
        e.preventDefault()
        
        // this will also be the partyID
        const resourceID = Number.parseInt( e.target.name, 10 )
        
        setTeamFilter([ resourceID ])
    }
    
    const resourceLabelContent = info => {
        
        const {title: name, id } = info.resource

        return <>
            <a href='/' name={id} onClick={handleResourceClick} >{name}</a>
            <button type='button' className='working-hours__open'
                title={'Working hours for ' + name} aria-label={'Working hours for ' + name}
                onClick={() => setCalendarEvent( { action: 'workingHours',
                    event: { partyID: Number.parseInt( id, 10 ), name } } )} >
                &#128339;
            </button>
        </>
    }
    
    const floatStyle = {
        float: 'left',
        marginRight: '1em'
    };
    
    /* warp with callback to reduce re-renders */
    const eventDrop = useCallback( 
            (info) => handleDrop( info, api, setCalendarEvent, rotaMode, calendarApi ), 
            [api, setCalendarEvent, rotaMode, calendarApi] );
    
    if( !teamID ) {
        return ''
    }else{
    return (
        <>
       
       <TeamCalendarOverlay api={api} calendarEvent={calendarEvent} 
                setCalendarEvent={setCalendarEvent} calendarApi={calendarApi} 
                state={state} eventDispatch={eventDispatch} />
        
                
        <h1 className={'calHead'} >{'\'' + teamName + '\' Team Calendar'}<Loading loading={loading} /></h1>

        <WebAPIWarning error={workingHoursError} title='Working hours not shown'
            impact={WORKING_HOURS_IMPACT} onRetry={retryWorkingHours} />

        <FullCalendar
            ref={calendarApi}
            plugins={[ resourceTimeGridPlugin, resourceDayGridPlugin, interactionPlugin ]}
            initialView='resourceTimeGridDay'
            locale='en-gb'
            views={{
              resourceTimeGridDay: { titleFormat: { weekday: 'short', year: 'numeric', month: 'short', day: '2-digit' } }
            }}
            businessHours= {defaultBusinessHours}
            initialDate={date}
            selectable={true}
            selectMirror={true}
            hiddenDays={[ 0 ] /* hide */ }  
            slotDuration={slotInterval}
            slotLabelInterval={slotLabelInterval}
            slotMinTime={slotMinTime}
            slotMaxTime='20:00:00'
            slotLabelFormat={{
                hour:               'numeric',
                minute:             '2-digit',
                omitZeroMinute:     omitZeroMinute,
                meridiem: 'short'
            }}
            scrollTime={dayStartTime}
            height= {'auto'}
            stickyHeaderDates={'auto'}
            loading={isLoading => setLoading(isLoading)}
            eventSources={eventSources}
            resources={resourceSource}
            refetchResourcesOnNavigate={true /* working hours depend on the date shown */}
            editable={true}
            eventResize={handleResize}
            eventClick={handleClick}
            eventContent={eventContent}
            dayHeaderContent={dayHeaderContent}
            eventDrop={eventDrop}
            select={handleSelectRegion}
            resourceLabelContent={resourceLabelContent}
            filterResourcesWithEvents={hideEmpty}
            customButtons={{
                clearResFilter: {
                    text: 'Everyone',
                    click: () => setTeamFilter([])
                }
            }}
            headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'clearResFilter,resourceTimeGridDay,resourceTimeGridWeek'
            }}
            
            schedulerLicenseKey={schedulerLicenseKey}
          />
          <div>
            <div style={floatStyle} >
                <label>Rota Mode</label>
                <input disabled={hasDiaryRotaPerm?'':'disabled'} type='checkbox' name='rotaMode'  onChange={handleModeChange} checked={rotaMode} />
            </div>
            <div style={floatStyle} >
                <label>Hide People not Working</label>
                <input type='checkbox' name='hideEmpty'  onChange={handleModeChange} checked={hideEmpty} />
            </div>
            <div style={floatStyle}>
                {false &&<><label>Quick Edit Mode</label>
                <input type='checkbox' name='qeMode'    onChange={handleModeChange} checked={qeMode} /></>}
            </div>
          </div>
          </>
    )
    }
}

export default TeamCalendar


